import React, { useMemo, useState } from 'react';

import classNames from 'classnames';
import { useQuery } from 'react-query';
import { Icon } from 'src/common/components/Icon';
import { OrbitIcons } from 'src/common/constants.';

import { fetchAccountRouting } from '../api';
import type {
  AccountRouting,
  AccountRoutingExtension,
  AccountRoutingInbound,
  AccountRoutingOutbound,
  AccountRoutingRegistration,
} from '../api';

const CONFIG_LABELS: Record<string, string> = {
  agent_route: 'agentes',
  message_route: 'mensaje',
  external_number_route: 'número externo',
};

const SCHEDULE_LABELS: Record<string, string> = {
  in_office: 'en horario',
  out_of_office: 'fuera de horario',
  time_off: 'días libres',
};

const WARNING_LABELS: Record<string, string> = {
  freeswitch_unreachable: 'No se pudo contactar el FreeSWITCH de la cuenta.',
  registrations_unavailable: 'No se pudieron leer las registraciones.',
  outbound_unavailable: 'No se pudieron leer los troncales de salida.',
};

/** `in_office:agent_route` → `en horario · agentes` */
function describeRoute(route: string): string {
  const [schedule, config] = route.split(':');
  return [SCHEDULE_LABELS[schedule] ?? schedule, CONFIG_LABELS[config] ?? config].join(' · ');
}

/**
 * Turns a dialplan expression into the digits an operator dials.
 *
 * `^9(\d{4})$` is how FreeSWITCH sees the route; `9 + 4 díg.` is how the person
 * on the phone sees it. Anything this does not recognise is shown verbatim
 * rather than guessed at.
 */
export function describePattern(pattern: string | null | undefined): string {
  if (!pattern) return '—';
  const body = pattern.replace(/^\^/, '').replace(/\$$/, '');
  if (/^\d+$/.test(body)) return body;

  const match = /^(\d*)\??\(?\\d\{(\d+)(?:,(\d+))?\}\)?$/.exec(body);
  if (match) {
    const [, prefix, from, to] = match;
    const digits = to ? `${from}-${to} díg.` : `${from} díg.`;
    return prefix ? `${prefix} + ${digits}` : digits;
  }
  return pattern;
}

const NODE_WIDTH = 208;
const NODE_HEIGHT = 62;
const NODE_GAP = 14;
const COLUMN_GAP = 76;
const PADDING = 16;
const CORE_X = PADDING + NODE_WIDTH + COLUMN_GAP;
const OUT_X = CORE_X + NODE_WIDTH + COLUMN_GAP;
const WIDTH = OUT_X + NODE_WIDTH + PADDING;
// How far the graph may shrink before the container scrolls instead: below this
// the 10px node labels stop being legible, and three columns of boxes side by
// side are worth a horizontal scrollbar.
const MIN_SCALE = 0.62;

type NodeKind = 'inbound' | 'core' | 'proxy' | 'internal' | 'outbound';

interface BaseNode {
  id: string;
  x: number;
  y: number;
  title: string;
  subtitle?: string | null;
  meta?: string | null;
  /** Set when the node is known to carry traffic, which thickens its edges. */
  active?: boolean;
  /** How much of the account's traffic the node holds, on outbound nodes. */
  traffic?: TrafficState;
}

/**
 * The routing entry a node was built from travels with it, so the detail panel
 * reads it instead of parsing the id back into a lookup: several DIDs on one call
 * module is the normal case, so an id is not a key of anything. `kind` carries
 * the narrowing, which is what keeps the panel from reading an inbound route out
 * of an outbound node without anything objecting.
 */
type GraphNode =
  | (BaseNode & { kind: 'inbound'; source: AccountRoutingInbound })
  | (BaseNode & { kind: 'outbound'; source: AccountRoutingOutbound })
  | (BaseNode & { kind: 'core' | 'proxy' | 'internal' });

interface GraphEdge {
  from: GraphNode;
  to: GraphNode;
  label?: string;
  active?: boolean;
}

function stack(count: number, height: number): number[] {
  const total = count * NODE_HEIGHT + Math.max(count - 1, 0) * NODE_GAP;
  const top = Math.max(PADDING, (height - total) / 2);
  return Array.from({ length: count }, (_, index) => top + index * (NODE_HEIGHT + NODE_GAP));
}

/** `1 extensión` rather than `1 extensiones`. */
function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

const DEVICE_KINDS: [string, string, string][] = [
  ['ws', 'navegador', 'navegadores'],
  ['other', 'teléfono físico', 'teléfonos físicos'],
  ['unknown', 'dispositivo sin identificar', 'dispositivos sin identificar'],
];

function deviceKind(registration: AccountRoutingRegistration): string {
  if (registration.transport === 'ws') return 'ws';
  return registration.transport ? 'other' : 'unknown';
}

/** `a`, `a y b`, `a, b y c` — a bare `join` only reads well for two. */
function enumerate(items: string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  return `${items.slice(0, -1).join(', ')} y ${items[items.length - 1]}`;
}

/**
 * An extension can be registered from the browser and from a desk phone at the
 * same time, so every device is counted by kind; a contact whose transport the
 * instance does not render is left unidentified rather than called hardware.
 */
function describeRegistrations(registrations: AccountRoutingRegistration[] | undefined): string {
  if (!registrations) return 'No se pudieron leer sus registraciones';
  if (registrations.length === 0) return 'Sin registrar ahora';

  const kinds = registrations.map(deviceKind);
  const counted = DEVICE_KINDS.filter(([kind]) => kinds.includes(kind)).map(([kind, one, many]) => {
    const count = kinds.filter((seen) => seen === kind).length;
    return count === 1 ? one : plural(count, one, many);
  });
  return `Registrada por ${enumerate(counted)}`;
}

function isRegistered(extension: AccountRoutingExtension): boolean {
  return (extension.registrations?.length ?? 0) > 0;
}

/**
 * The instance reports call counters as strings, so a value it does not render as
 * a number is `null` rather than a count: zero would say the trunk is configured
 * and idle, which is a different story from not having been able to read it.
 */
function callCount(value: string | null | undefined): number | null {
  if (value == null || value === '') return 0;
  const count = Number(value);
  return Number.isFinite(count) ? count : null;
}

/**
 * Share of the account's outbound calls under which a trunk reads as residual
 * instead of in use. A gateway left behind by an old carrier keeps its counter,
 * so 'carried a call at some point' is not 'is the trunk the account calls
 * through': a trunk holding 1% of what the main carrier holds is a leftover.
 */
const RESIDUAL_SHARE = 0.05;

type TrafficState =
  /** Carries a share of the outbound calls worth calling the account's trunk. */
  | 'in-use'
  /** Configured and reachable, with a share too small to be the route in use. */
  | 'residual'
  /** Configured, and no call went through it in what was counted. */
  | 'idle'
  /** The instance answered with a counter it did not render as a number. */
  | 'unknown'
  /** A direct bridge to a host, on an instance that counted no recent calls. */
  | 'unmeasured';

interface Traffic {
  state: TrafficState;
  /** Calls the state was decided on, or null when none could be counted. */
  calls: number | null;
  /** Whether those calls are recent ones rather than the gateway's own counter. */
  recent: boolean;
}

/**
 * Reads how much of the account's outbound traffic a route carries.
 *
 * Recent calls decide it whenever interstellar counted them: a gateway's counter
 * runs from the moment the profile loaded it and no carrier switch resets it, so
 * a trunk the account stopped using keeps every call it ever placed and outranks
 * the one carrying the traffic today. The counters stay the fallback for an
 * interstellar that does not send recent calls, and for an account that dialled
 * nothing in the window — there, recent calls say nothing about which trunk it
 * uses, only that it made no calls.
 *
 * Which of the two the account is read on is decided once for all its routes
 * (`totals.recent`), never per route: a share is only a share of the same total.
 */
function routeTraffic(route: AccountRoutingOutbound, totals: Totals): Traffic {
  if (totals.recent != null && route.recent_calls != null) {
    const calls = route.recent_calls;
    if (calls === 0) return { state: 'idle', calls, recent: true };
    const share = calls >= totals.recent * RESIDUAL_SHARE;
    return { state: share ? 'in-use' : 'residual', calls, recent: true };
  }
  if (route.kind !== 'gateway') return { state: 'unmeasured', calls: null, recent: false };
  // A counter the instance did not send is as unreadable as one it did not render
  // as a number: an interstellar that predates the counters sends neither.
  if (route.calls_out == null || route.calls_out === '') {
    return { state: 'unknown', calls: null, recent: false };
  }
  const calls = callCount(route.calls_out);
  if (calls === null) return { state: 'unknown', calls: null, recent: false };
  if (calls === 0) return { state: 'idle', calls, recent: false };
  const share = calls >= totals.cumulative * RESIDUAL_SHARE;
  return { state: share ? 'in-use' : 'residual', calls, recent: false };
}

interface Totals {
  /**
   * Calls the account placed within the recent window, or null when they are
   * not what its routes are read on.
   */
  recent: number | null;
  /** Calls the gateways report having placed since they were loaded. */
  cumulative: number;
}

/**
 * The totals a route's share is measured against.
 *
 * Recent calls are only the basis when every route reports them and the account
 * placed a call: one route judged on its share of the window while another is
 * judged on its share of the gateways' lifetime counters puts the two on
 * different denominators, and the leftover trunk this reading exists to demote
 * wins its comparison again.
 */
function outboundTotals(outbound: AccountRoutingOutbound[]): Totals {
  const recent = outbound.reduce((total, route) => total + (route.recent_calls ?? 0), 0);
  const counted = outbound.length > 0 && outbound.every((route) => route.recent_calls != null);
  return {
    recent: counted && recent > 0 ? recent : null,
    cumulative: outbound.reduce((total, route) => total + (callCount(route.calls_out) ?? 0), 0),
  };
}

const TRAFFIC_LABELS: Record<TrafficState, string> = {
  'in-use': 'en uso',
  residual: 'residual',
  idle: 'sin tráfico',
  unknown: 'tráfico desconocido',
  // Reached only by a route that is not a gateway, which is why nothing counted it.
  unmeasured: 'bridge directo, sin gateway',
};

/** A trunk is dimmed only when the instance said enough to call it unused. */
function isDimmed(state: TrafficState): boolean {
  return state === 'residual' || state === 'idle';
}

/**
 * What names a count as a recent one, wherever it is rendered.
 *
 * A recent count says how much the account calls through the trunk and a
 * cumulative one how much it ever did, so a recent count never renders bare:
 * without the window it is still named for what it counts.
 */
function recentSuffix(windowDays: number | undefined): string {
  return windowDays ? ` en ${windowDays} días` : ' recientes';
}

/**
 * What a trunk's traffic reads as on its node: the count when there is one to
 * show, and the state's own name for everything else, so the node, the `Uso` row
 * and the edge list cannot disagree about the same trunk.
 */
function describeTraffic(traffic: Traffic, windowDays: number | undefined): string {
  if (traffic.calls === null || traffic.calls === 0) return TRAFFIC_LABELS[traffic.state];
  const suffix = traffic.recent ? recentSuffix(windowDays) : '';
  const count = `${traffic.calls.toLocaleString('es-PR')} llam.${suffix}`;
  return traffic.state === 'in-use' ? count : `${count} · ${TRAFFIC_LABELS[traffic.state]}`;
}

function countLabel(value: string | null | undefined): string {
  const count = callCount(value);
  return count === null ? '—' : count.toLocaleString('es-PR');
}

function extensionRange(extensions: AccountRoutingExtension[]): string | null {
  if (extensions.length === 0) return null;
  const numbers = extensions.map((extension) => extension.extension);
  const low = Math.min(...numbers);
  const high = Math.max(...numbers);
  return low === high ? `${low}` : `${low}–${high}`;
}

/**
 * Lays the account's routing out as three columns: what comes in, the instance
 * that answers, and what calls leave through. Positions are computed rather than
 * measured so the same graph renders identically without a layout engine.
 */
function buildGraph(routing: AccountRouting): {
  nodes: GraphNode[];
  edges: GraphEdge[];
  height: number;
} {
  const inbound = routing.inbound;
  const outbound = routing.outbound;
  // Everything that starts a call is on the left: the numbers that come in and
  // the extensions that dial out. The middle column is the instance and the
  // proxy it leaves through, in the order a call traverses them.
  const sources = inbound.length + 1;
  const coreCount = routing.outbound_proxy ? 2 : 1;
  const rows = Math.max(sources, outbound.length, coreCount);
  const height = rows * NODE_HEIGHT + Math.max(rows - 1, 0) * NODE_GAP + PADDING * 2;

  const sourceY = stack(sources, height);
  const inboundNodes: GraphNode[] = inbound.map((route: AccountRoutingInbound, index) => ({
    id: `in-${index}-${route.call_module_id}`,
    kind: 'inbound',
    x: PADDING,
    y: sourceY[index],
    title: route.number,
    subtitle: route.call_module,
    meta: route.routes.map(describeRoute).join(' / ') || route.schedule,
    active: true,
    source: route,
  }));

  const totals = outboundTotals(outbound);
  const outboundNodes: GraphNode[] = stack(outbound.length, height).map((y, index) => {
    const route: AccountRoutingOutbound = outbound[index];
    const traffic = routeTraffic(route, totals);
    return {
      id: `out-${index}-${route.kind}-${route.name}`,
      kind: 'outbound',
      x: OUT_X,
      y,
      title: route.name,
      subtitle: route.host,
      meta: [
        describeTraffic(traffic, routing.recent_window_days),
        route.kind === 'gateway' && (route.register ? route.state : 'autenticado por IP'),
      ]
        .filter(Boolean)
        .join(' · '),
      active: traffic.state === 'in-use',
      traffic: traffic.state,
      source: route,
    };
  });

  const coreY = stack(coreCount, height);
  const core: GraphNode = {
    id: 'core',
    kind: 'core',
    x: CORE_X,
    y: coreY[0],
    title: 'FreeSWITCH',
    subtitle: routing.freeswitch_host,
    active: true,
  };
  const nodes: GraphNode[] = [core, ...inboundNodes, ...outboundNodes];
  const edges: GraphEdge[] = inboundNodes.map((node) => ({
    from: node,
    to: core,
    label: 'entrante',
    active: true,
  }));

  const registered = routing.internal.filter(isRegistered);
  const internal: GraphNode = {
    id: 'internal',
    kind: 'internal',
    x: PADDING,
    y: sourceY[sources - 1],
    title: plural(routing.internal.length, 'extensión', 'extensiones'),
    subtitle: extensionRange(routing.internal),
    meta: `${plural(registered.length, 'registrada', 'registradas')} ahora`,
    active: registered.length > 0,
  };
  nodes.push(internal);
  edges.push({ from: internal, to: core, label: 'internas', active: registered.length > 0 });

  let outboundSource = core;
  if (routing.outbound_proxy) {
    const proxy: GraphNode = {
      id: 'proxy',
      kind: 'proxy',
      x: CORE_X,
      y: coreY[1],
      title: 'Outbound proxy',
      subtitle: routing.outbound_proxy,
      meta: 'todo el saliente pasa por aquí',
      active: true,
    };
    nodes.push(proxy);
    edges.push({ from: core, to: proxy, active: true });
    outboundSource = proxy;
  }

  outboundNodes.forEach((node, index) => {
    edges.push({
      from: outboundSource,
      to: node,
      label: outbound[index].patterns.map(describePattern).join(', ') || undefined,
      active: node.active,
    });
  });

  return { nodes, edges, height };
}

const NODE_STYLES: Record<NodeKind, { fill: string; stroke: string }> = {
  inbound: { fill: '#EFF6FF', stroke: '#93C5FD' },
  core: { fill: '#111827', stroke: '#111827' },
  proxy: { fill: '#F5F3FF', stroke: '#C4B5FD' },
  internal: { fill: '#F0FDF4', stroke: '#86EFAC' },
  outbound: { fill: '#FFF7ED', stroke: '#FDBA74' },
};

/** Fade of a trunk the instance reported as unused, on its box alone. */
const DIMMED_OPACITY = 0.55;
const BADGE_INSET = 16;
const BADGE_RADIUS = 7.5;
const IN_USE_FILL = '#16A34A';

/**
 * Marks the trunk the account calls through. Decorative: the state it stands for
 * is in the node's accessible name, and the check itself carries the meaning so
 * the green is not the only cue.
 */
const InUseBadge: React.FC<{ x: number; y: number }> = ({ x, y }) => (
  <g aria-hidden="true">
    <circle cx={x} cy={y} r={BADGE_RADIUS} fill={IN_USE_FILL} />
    <path
      d={`M ${x - 3.5} ${y} l 2.5 2.5 l 4.5 -5`}
      fill="none"
      stroke="#FFFFFF"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </g>
);

function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

/** A horizontal edge, curved so parallel edges stay readable. */
function edgePath(edge: GraphEdge): string {
  const from = edge.from;
  const to = edge.to;
  if (from.x === to.x) {
    const start = from.y + NODE_HEIGHT;
    return `M ${from.x + NODE_WIDTH / 2} ${start} L ${to.x + NODE_WIDTH / 2} ${to.y}`;
  }
  const x1 = from.x + NODE_WIDTH;
  const y1 = from.y + NODE_HEIGHT / 2;
  const x2 = to.x;
  const y2 = to.y + NODE_HEIGHT / 2;
  const mid = x1 + (x2 - x1) / 2;
  return `M ${x1} ${y1} C ${mid} ${y1}, ${mid} ${y2}, ${x2} ${y2}`;
}

const NodeDetail: React.FC<{ node: GraphNode; routing: AccountRouting }> = ({ node, routing }) => {
  const rows: [string, string][] = [];

  if (node.kind === 'inbound') {
    const inbound = node.source;
    rows.push(['Número', inbound.number]);
    rows.push(['Módulo', `${inbound.call_module} (#${inbound.call_module_id})`]);
    if (inbound.schedule) rows.push(['Horario', inbound.schedule]);
    if (inbound.routes.length > 0) {
      rows.push(['Destinos', inbound.routes.map(describeRoute).join(' / ')]);
    }
  }

  if (node.kind === 'outbound') {
    const outbound = node.source;
    rows.push(['Destino', outbound.name]);
    rows.push([outbound.kind === 'gateway' ? 'Gateway' : 'Host', outbound.host ?? '—']);
    rows.push([
      'Se marca',
      outbound.patterns.map(describePattern).join(', ') || 'sin ruta en el dialplan',
    ]);
    if (outbound.kind === 'gateway') {
      rows.push([
        'Registro',
        outbound.register ? outbound.state ?? '—' : 'no registra (autenticado por IP)',
      ]);
      // Cumulative, and labelled as such: they run from the moment the profile
      // loaded the gateway, so they are history rather than what it carries.
      rows.push([
        'Acumuladas',
        `${countLabel(outbound.calls_out)} salientes · ${countLabel(outbound.calls_in)} entrantes`,
      ]);
    }
    if (outbound.recent_calls != null) {
      rows.push([
        'Recientes',
        `${outbound.recent_calls.toLocaleString('es-PR')} salientes${recentSuffix(
          routing.recent_window_days
        )}`,
      ]);
    }
    if (node.traffic) rows.push(['Uso', TRAFFIC_LABELS[node.traffic]]);
  }

  if (node.kind === 'internal') {
    return (
      <div className="mt-3 rounded border border-white-2.0 bg-white-0.0 p-3 shadow-sm">
        <p className="mb-2 text-xs font-bold uppercase tracking-wide text-gray-3.0">Extensiones</p>
        <div className="flex flex-wrap gap-1.5">
          {routing.internal.map((extension) => (
            <span
              key={extension.extension}
              // The devices are the point of the badge's colour, so they are also
              // announced and not left to a pointer hovering over a tooltip.
              title={describeRegistrations(extension.registrations)}
              aria-label={`${extension.extension}${
                extension.name ? ` · ${extension.name}` : ''
              }, ${describeRegistrations(extension.registrations)}`}
              className={classNames(
                'inline-flex items-center rounded px-2 py-1 font-mono text-xs font-semibold',
                isRegistered(extension)
                  ? 'bg-green-100 text-green-800'
                  : 'bg-white-2.0 text-gray-3.0'
              )}
            >
              {extension.extension}
              {extension.name ? ` · ${extension.name}` : ''}
            </span>
          ))}
        </div>
      </div>
    );
  }

  if (node.kind === 'core' || node.kind === 'proxy') {
    rows.push(['Host', node.subtitle ?? '—']);
  }

  if (rows.length === 0) return null;

  return (
    <dl className="mt-3 rounded border border-white-2.0 bg-white-0.0 p-3 shadow-sm">
      {rows.map(([label, value]) => (
        <div key={label} className="flex gap-3 border-b border-white-2.0 py-1.5 last:border-b-0">
          <dt className="w-28 flex-shrink-0 text-xs font-medium text-gray-2.0">{label}</dt>
          <dd className="text-xs font-semibold text-black-1.0">{value}</dd>
        </div>
      ))}
    </dl>
  );
};

interface RoutingGraphProps {
  accountId: number;
}

export const RoutingGraph: React.FC<RoutingGraphProps> = ({ accountId }) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Browsers paint no focus ring on an SVG <g>, so tabbing through the nodes
  // would move nothing on screen: the node draws its own.
  const [focusedId, setFocusedId] = useState<string | null>(null);

  const {
    data: routing,
    isLoading,
    isError,
    refetch,
    isFetching,
  } = useQuery(['interstellar-account-routing', accountId], () => fetchAccountRouting(accountId), {
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const graph = useMemo(() => (routing ? buildGraph(routing) : null), [routing]);
  const incoming = useMemo(() => {
    const counts = new Map<string, number>();
    graph?.edges.forEach((edge) => counts.set(edge.to.id, (counts.get(edge.to.id) ?? 0) + 1));
    return counts;
  }, [graph]);
  const selected = graph?.nodes.find((node) => node.id === selectedId) ?? null;

  if (isLoading) {
    return <p className="p-6 text-sm text-gray-2.0">Leyendo el enrutamiento de la cuenta…</p>;
  }

  if (isError || !routing || !graph) {
    return (
      <div className="p-6">
        <p className="text-sm text-gray-2.0">
          No se pudo leer el enrutamiento. Comprueba permisos de API o Interstellar.
        </p>
        <button
          type="button"
          onClick={() => refetch()}
          className="mt-3 rounded border border-white-2.0 px-3 py-1.5 text-xs font-semibold text-black-1.0 hover:bg-white-1.0"
        >
          Reintentar
        </button>
      </div>
    );
  }

  return (
    <section className="mb-6">
      <div className="flex items-center justify-between gap-2 mb-3">
        <h3 className="flex items-center gap-2.5 text-xs font-bold uppercase tracking-wide text-gray-3.0">
          <span className="flex h-8 w-8 items-center justify-center rounded bg-white-2.0">
            <Icon name={OrbitIcons.Workflow} size={16} className="text-primary-2.0" />
          </span>
          Enrutamiento
        </h3>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          title="Vuelve a leerlo de la instancia; no se guarda nada"
          className="rounded border border-white-2.0 px-3 py-1.5 text-xs font-semibold text-black-1.0 hover:bg-white-1.0 disabled:opacity-50"
        >
          {isFetching ? 'Leyendo…' : 'Releer'}
        </button>
      </div>

      {routing.warnings.length > 0 && (
        <div className="bg-yellow-50 px-3 py-2 mb-3 border border-yellow-200 rounded">
          {routing.warnings.map((warning) => (
            <p key={warning} className="text-xs font-medium text-yellow-800">
              {WARNING_LABELS[warning] ?? warning}
            </p>
          ))}
        </div>
      )}

      <div className="overflow-x-auto rounded border border-white-2.0 bg-white-0.0 p-2 shadow-sm">
        <svg
          role="group"
          aria-label={`Enrutamiento de ${routing.name}`}
          viewBox={`0 0 ${WIDTH} ${graph.height}`}
          width="100%"
          style={{ minWidth: WIDTH * MIN_SCALE }}
        >
          <defs>
            <marker
              id="routing-arrow"
              viewBox="0 0 8 8"
              refX="7"
              refY="4"
              markerWidth="7"
              markerHeight="7"
              orient="auto"
            >
              <path d="M 0 0 L 8 4 L 0 8 z" fill="#9CA3AF" />
            </marker>
          </defs>

          {graph.edges.map((edge) => {
            // Against whichever end of the edge is its own, never at the midpoint:
            // parallel edges all cross the middle of the gap, so labels there land
            // on the curves and on each other.
            const shared = incoming.get(edge.to.id) ?? 0;
            const atSource = shared > 1;
            const label = atSource ? edge.from : edge.to;
            const labelX = atSource ? label.x + NODE_WIDTH + 8 : label.x - 8;
            const labelY = label.y + NODE_HEIGHT / 2 - 8;
            return (
              <g key={`${edge.from.id}->${edge.to.id}`}>
                <path
                  d={edgePath(edge)}
                  fill="none"
                  stroke={edge.active ? '#6B7280' : '#D1D5DB'}
                  strokeWidth={edge.active ? 2 : 1.25}
                  strokeDasharray={edge.active || edge.to.traffic === 'unknown' ? undefined : '4 4'}
                  markerEnd="url(#routing-arrow)"
                />
                {edge.label && edge.from.x !== edge.to.x && (
                  <text
                    x={labelX}
                    y={labelY}
                    textAnchor={atSource ? 'start' : 'end'}
                    className="fill-gray-2.0"
                    fontSize="10"
                  >
                    {truncate(edge.label, 26)}
                  </text>
                )}
              </g>
            );
          })}

          {graph.nodes.map((node) => {
            const style = NODE_STYLES[node.kind];
            const dark = node.kind === 'core';
            const dimmed = node.traffic ? isDimmed(node.traffic) : false;
            const toggle = (): void => setSelectedId(node.id === selectedId ? null : node.id);
            return (
              // Every node opens the detail panel, so it has to be a control: the
              // text inside an SVG is not reachable on its own, and role="img" on
              // the diagram would hide all of it behind a single label.
              <g
                key={node.id}
                role="button"
                tabIndex={0}
                aria-pressed={node.id === selectedId}
                aria-label={[
                  node.title,
                  node.subtitle,
                  node.meta,
                  // The check that marks a trunk in use is decorative, so the
                  // state it stands for is announced here; the rest of them are
                  // already spelled out in the node's own meta line.
                  node.traffic === 'in-use' ? TRAFFIC_LABELS[node.traffic] : null,
                ]
                  .filter(Boolean)
                  .join(', ')}
                onClick={toggle}
                onFocus={() => setFocusedId(node.id)}
                onBlur={() => setFocusedId((current) => (current === node.id ? null : current))}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    toggle();
                  }
                }}
                style={{ cursor: 'pointer', outline: 'none' }}
              >
                <rect
                  x={node.x}
                  y={node.y}
                  width={NODE_WIDTH}
                  height={NODE_HEIGHT}
                  rx="6"
                  fill={style.fill}
                  stroke={
                    node.id === selectedId || node.id === focusedId ? '#2563EB' : style.stroke
                  }
                  strokeWidth={node.id === selectedId ? 2 : 1}
                  strokeDasharray={node.id === focusedId ? '4 2' : undefined}
                  // Only the box is faded: fading the text with it drops the node
                  // below the contrast a small label needs to stay readable, and a
                  // residual trunk has to be read, not just noticed.
                  fillOpacity={dimmed ? DIMMED_OPACITY : 1}
                  strokeOpacity={dimmed ? DIMMED_OPACITY : 1}
                />
                <text
                  x={node.x + 12}
                  y={node.y + 22}
                  fontSize="12"
                  fontWeight="700"
                  fill={dark ? '#F9FAFB' : '#111827'}
                >
                  {truncate(node.title, 26)}
                </text>
                {node.subtitle && (
                  <text
                    x={node.x + 12}
                    y={node.y + 38}
                    fontSize="10"
                    fill={dark ? '#D1D5DB' : '#4B5563'}
                  >
                    {truncate(node.subtitle, 32)}
                  </text>
                )}
                {node.meta && (
                  <text
                    x={node.x + 12}
                    y={node.y + 52}
                    fontSize="9"
                    fill={dark ? '#9CA3AF' : '#6B7280'}
                  >
                    {truncate(node.meta, 36)}
                  </text>
                )}
                {node.traffic === 'in-use' && (
                  <InUseBadge x={node.x + NODE_WIDTH - BADGE_INSET} y={node.y + BADGE_INSET} />
                )}
              </g>
            );
          })}
        </svg>
      </div>

      {/*
        The edges carry the meaning of the diagram - which number reaches which
        trunk, and with what digits - and a label per node says nothing about
        them, so they are also written out for anyone not seeing the drawing.
      */}
      <ul className="sr-only">
        {graph.edges.map((edge) => (
          <li key={`${edge.from.id}->${edge.to.id}`}>
            {[edge.from.title, edge.to.title].join(' → ')}
            {edge.label ? `: ${edge.label}` : ''}
            {edge.to.traffic ? ` (${TRAFFIC_LABELS[edge.to.traffic]})` : ''}
          </li>
        ))}
      </ul>

      {selected && <NodeDetail node={selected} routing={routing} />}

      <p className="mt-2 text-[11px] text-gray-2.0">
        Se lee de la instancia cada vez que abres esta pestaña; la columna SIP Trunk del listado es
        un resumen guardado. Check verde = troncal por el que sale la cuenta; línea punteada y
        atenuada = configurado pero residual o sin uso.
      </p>
    </section>
  );
};
